import "@/App.css";
import { useEffect } from "react";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { HelmetProvider } from "react-helmet-async";
import { AnimatePresence, motion } from "framer-motion";
import LandingPage from "./pages/LandingPage";
import ReferencePage from "./pages/ReferencePage";
import TechnikaPage from "./pages/TechnikaPage";
import NotFoundPage from "./pages/NotFoundPage";
import AdminLogin from "./pages/admin/AdminLogin";
import AdminLayout from "./pages/admin/AdminLayout";
import AdminDashboard from "./pages/admin/AdminDashboard";
import AdminReferencie from "./pages/admin/AdminReferencie";
import AdminTechnika from "./pages/admin/AdminTechnika";
import AdminTestimonials from "./pages/admin/AdminTestimonials";
import AdminSluzby from "./pages/admin/AdminSluzby";
import AdminSluzbaEdit from "./pages/admin/AdminSluzbaEdit";
import AdminFacebook from "./pages/admin/AdminFacebook";
import AdminGoogle from "./pages/admin/AdminGoogle";
import AdminContacts from "./pages/admin/AdminContacts";
import VizualizaciePage from "./pages/VizualizaciePage";
import VizualizaciaDetailPage from "./pages/VizualizaciaDetailPage";
import ReferenciaDetailPage from "./pages/ReferenciaDetailPage";
import ServiceDetailPage from "./pages/ServiceDetailPage";
import RentalPage from "./pages/RentalPage";
import AdamHallPage from "./pages/AdamHallPage";
import ONasPage from "./pages/ONasPage";
import KontaktPage from "./pages/KontaktPage";
import AdminBookings from "./pages/admin/AdminBookings";
import AdminBackup from "./pages/admin/AdminBackup";
import AdminBackupSchedule from "./pages/admin/AdminBackupSchedule";
import AdminVizualizacie from "./pages/admin/AdminVizualizacie";
import AdminBrands from "./pages/admin/AdminBrands";
import AdminSettings from "./pages/admin/AdminSettings";
import AdminUsers from "./pages/admin/AdminUsers";
import AdminSecurity from "./pages/admin/AdminSecurity";
import AdminAuditLog from "./pages/admin/AdminAuditLog";
import AdminStats from "./pages/admin/AdminStats";
import { AdminProvider } from "./context/AdminContext";
import { CartProvider } from "./context/CartContext";
import { Toaster } from "sonner";
import ScrollProgress from "./components/ui/ScrollProgress";
import CookieBanner from "./components/ui/CookieBanner";
import { api } from "./services/api";

function ScrollToTop() {
    const { pathname } = useLocation();
    useEffect(() => { window.scrollTo(0, 0); }, [pathname]);
    return null;
}

function PageTracker() {
    const { pathname } = useLocation();
    useEffect(() => {
        if (pathname.startsWith("/admin")) return;
        api.recordHit(pathname, document.referrer).catch(() => {});
    }, [pathname]);
    return null;
}

// ── Page transition wrapper ───────────────────────────────────────────────────
// Admin sub-pages share one key so internal admin navigation has no transition.
// Each public page has its own key → plays fade+slide on route change.
function AnimatedRoutes() {
    const location = useLocation();
    const key = location.pathname.startsWith("/admin")
        ? "admin"
        : location.pathname;

    return (
        <AnimatePresence mode="wait" initial={false}>
            <motion.div
                key={key}
                initial={{ opacity: 0, y: 8 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.22, ease: [0.4, 0, 0.2, 1] }}
                style={{ isolation: "isolate" }}
            >
                <Routes location={location}>
                    {/* Public */}
                    <Route path="/"            element={<LandingPage />} />
                    <Route path="/referencie"  element={<ReferencePage />} />
                    <Route path="/referencie/:id" element={<ReferenciaDetailPage />} />
                    <Route path="/technika"    element={<TechnikaPage />} />
                    <Route path="/vizualizacie" element={<VizualizaciePage />} />
                    <Route path="/vizualizacie/:id" element={<VizualizaciaDetailPage />} />
                    <Route path="/sluzby/:slug" element={<ServiceDetailPage />} />
                    <Route path="/prenajom"     element={<RentalPage />} />
                    <Route path="/predaj"       element={<AdamHallPage />} />
                    <Route path="/o-nas"        element={<ONasPage />} />
                    <Route path="/kontakt"      element={<KontaktPage />} />

                    {/* Admin */}
                    <Route path="/admin/login" element={<AdminLogin />} />
                    <Route path="/admin" element={<AdminLayout />}>
                        <Route index element={<Navigate to="referencie" replace />} />
                        <Route path="dashboard"    element={<AdminDashboard />} />
                        <Route path="referencie"   element={<AdminReferencie />} />
                        <Route path="technika"     element={<AdminTechnika />} />
                        <Route path="testimonials" element={<AdminTestimonials />} />
                        <Route path="sluzby"          element={<AdminSluzby />} />
                        <Route path="sluzby/:slug"    element={<AdminSluzbaEdit />} />
                        <Route path="facebook"     element={<AdminFacebook />} />
                        <Route path="google"       element={<AdminGoogle />} />
                        <Route path="contacts"     element={<AdminContacts />} />
                        <Route path="bookings"     element={<AdminBookings />} />
                        <Route path="backup"          element={<AdminBackup />} />
                        <Route path="backup-schedule" element={<AdminBackupSchedule />} />
                        <Route path="vizualizacie" element={<AdminVizualizacie />} />
                        <Route path="brands"       element={<AdminBrands />} />
                        <Route path="settings"     element={<AdminSettings />} />
                        <Route path="users"        element={<AdminUsers />} />
                        <Route path="security"     element={<AdminSecurity />} />
                        <Route path="audit"        element={<AdminAuditLog />} />
                        <Route path="stats"        element={<AdminStats />} />
                    </Route>

                    {/* 404 */}
                    <Route path="*" element={<NotFoundPage />} />
                </Routes>
            </motion.div>
        </AnimatePresence>
    );
}

// ── App ───────────────────────────────────────────────────────────────────────
function App() {
    return (
        <HelmetProvider>
            <div className="App">
                <BrowserRouter>
                    <CartProvider>
                        <AdminProvider>
                            <ScrollToTop />
                            <PageTracker />
                            <ScrollProgress />
                            <AnimatedRoutes />
                        </AdminProvider>
                    </CartProvider>
                </BrowserRouter>
                <Toaster position="top-right" theme="dark" richColors />
                <CookieBanner />
            </div>
        </HelmetProvider>
    );
}

export default App;
